import { Component, type ErrorInfo, type ReactNode } from "react";
import { Alert } from "antd";

import { sentryErrorTrackingService } from "~/store/sentryErrorTrackingService";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

/**
 * Error boundary that catches rendering errors and shows a fallback UI.
 * Use for isolating errors in list items or other components.`
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    sentryErrorTrackingService.recordError(error, "ErrorBoundary");
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return <Alert message="Błąd wyświetlania" description={this.state.error?.message} type="error" showIcon />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
